import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './LandingPageManager.css';
import landingPageService from '../../services/landingPageService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';
const LANDING_API = `${API_URL}/landing_page_api.php`;

// Page section_content is stored as raw HTML (rendered via dangerouslySetInnerHTML
// on the public About/Privacy/Terms pages) - strip tags for a readable plain-text
// preview snippet instead of showing the literal markup.
const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const LandingPageManager = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('carousel');
    const [message, setMessage] = useState({ type: '', text: '' });

    // Data states
    const [sections, setSections] = useState([]);
    const [carousel, setCarousel] = useState([]);
    const [features, setFeatures] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [faq, setFaq] = useState([]);
    const [blog, setBlog] = useState([]);
    const [pages, setPages] = useState([]);
    const [config, setConfig] = useState([]);
    const [contactMessages, setContactMessages] = useState([]);

    // Edit states
    const [editingItem, setEditingItem] = useState(null);

    useEffect(() => {
        loadContent();
    }, []);

    const loadContent = async () => {
        try {
            setLoading(true);
            const response = await axios.get(LANDING_API);

            if (response.data.status === 'success') {
                const data = response.data.data;
                setCarousel(data.carousel || []);
                setFeatures(data.features || []);
                setTestimonials(data.testimonials || []);
                setFaq(data.faq || []);
                setBlog(data.blog || []);
            }

            // Sections load from the admin-only endpoint (not the public one
            // above) so a deactivated section/content item - invisible to
            // the public site by design - still shows up here with its
            // Inactive badge and can be edited/re-activated.
            const sectionsResponse = await axios.get(`${LANDING_API}/admin/sections`, getAuthHeaders());
            if (sectionsResponse.data.status === 'success') {
                setSections(sectionsResponse.data.data || []);
            }

            // Load pages list
            const pagesResponse = await landingPageService.getPagesList();
            if (pagesResponse.status === 'success') {
                setPages(pagesResponse.data || []);
            }

            // Load configuration
            const configResponse = await axios.get(`${LANDING_API}/admin/config`, getAuthHeaders());
            if (configResponse.data.status === 'success') {
                setConfig(configResponse.data.data || []);
            }

            // Load contact messages
            const messagesResponse = await axios.get(`${LANDING_API}/admin/contact/messages`, getAuthHeaders());
            if (messagesResponse.data.status === 'success') {
                setContactMessages(messagesResponse.data.data || []);
            }
        } catch (error) {
            showMessage('error', 'Failed to load content: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    const getToken = () => localStorage.getItem('haazri_token');

    const getAuthHeaders = () => ({
        headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
        }
    });

    // Generic save handler
    const handleSave = async (endpoint, item, isUpdate = false) => {
        try {
            setSaving(true);
            console.log('Saving item:', item);
            console.log('Is update:', isUpdate);
            const url = isUpdate ? `${LANDING_API}/admin/${endpoint}/${item.id}` : `${LANDING_API}/admin/${endpoint}`;
            console.log('URL:', url);
            const method = isUpdate ? 'put' : 'post';
            const response = await axios[method](url, item, getAuthHeaders());
            console.log('Response:', response.data);

            if (response.data.status === 'success') {
                showMessage('success', 'Saved successfully');
                loadContent();
                setEditingItem(null);
            } else {
                showMessage('error', 'Failed to save: ' + (response.data.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Save error:', error);
            showMessage('error', 'Failed to save: ' + (error.response?.data?.message || error.message));
        } finally {
            setSaving(false);
        }
    };

    // Generic delete handler
    const handleDelete = async (endpoint, id, itemName) => {
        if (!window.confirm(`Are you sure you want to delete this ${itemName}?`)) return;

        try {
            setSaving(true);
            await axios.delete(`${LANDING_API}/admin/${endpoint}/${id}`, getAuthHeaders());
            showMessage('success', 'Deleted successfully');
            loadContent();
        } catch (error) {
            showMessage('error', 'Failed to delete: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="landing-page-manager">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading landing page content...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="landing-page-manager">
            {/* Header */}
            <div className="lpm-header">
                <div className="lpm-header-content">
                    <div>
                        <h1>Landing Page Manager</h1>
                        <p>Manage all landing page content and sections</p>
                    </div>
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                    >
                        <i className="fas fa-eye"></i>
                        Preview Landing Page
                    </a>
                </div>
            </div>

            {/* Message */}
            {message.text && (
                <div className={`lpm-message ${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            {/* Tabs */}
            <div className="lpm-tabs">
                {[
                    { key: 'carousel', label: 'Carousel', icon: 'image' },
                    { key: 'features', label: 'Features', icon: 'star' },
                    { key: 'testimonials', label: 'Testimonials', icon: 'quote-left' },
                    { key: 'faq', label: 'FAQ', icon: 'question-circle' },
                    { key: 'blog', label: 'Blog', icon: 'newspaper' },
                    { key: 'pages', label: 'About/Privacy/Terms', icon: 'file-text' },
                    { key: 'content', label: 'Page Content', icon: 'file-alt' },
                    { key: 'config', label: 'Configuration', icon: 'cog' },
                    { key: 'contact', label: 'Contact Messages', icon: 'envelope' }
                ].map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        className={`lpm-tab ${activeTab === tab.key ? 'active' : ''}`}
                    >
                        <i className={`fas fa-${tab.icon}`}></i>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content Area */}
            <div className="lpm-content">
                {activeTab === 'carousel' && (
                    <CarouselManager
                        items={carousel}
                        onSave={(item) => handleSave('carousel', item, !!item.id)}
                        onDelete={(id) => handleDelete('carousel', id, 'carousel item')}
                        editingItem={editingItem}
                        setEditingItem={setEditingItem}
                        saving={saving}
                    />
                )}

                {activeTab === 'features' && (
                    <FeaturesManager
                        items={features}
                        onSave={(item) => handleSave('features', item, !!item.id)}
                        onDelete={(id) => handleDelete('features', id, 'feature')}
                        editingItem={editingItem}
                        setEditingItem={setEditingItem}
                        saving={saving}
                    />
                )}

                {activeTab === 'testimonials' && (
                    <TestimonialsManager
                        items={testimonials}
                        onSave={(item) => handleSave('testimonials', item, !!item.id)}
                        onDelete={(id) => handleDelete('testimonials', id, 'testimonial')}
                        editingItem={editingItem}
                        setEditingItem={setEditingItem}
                        saving={saving}
                    />
                )}

                {activeTab === 'faq' && (
                    <FAQManager
                        items={faq}
                        onSave={(item) => handleSave('faq', item, !!item.id)}
                        onDelete={(id) => handleDelete('faq', id, 'FAQ')}
                        editingItem={editingItem}
                        setEditingItem={setEditingItem}
                        saving={saving}
                    />
                )}

                {activeTab === 'blog' && (
                    <BlogManager
                        items={blog}
                        onSave={(item) => handleSave('blog', item, !!item.id)}
                        onDelete={(id) => handleDelete('blog', id, 'blog post')}
                        editingItem={editingItem}
                        setEditingItem={setEditingItem}
                        saving={saving}
                    />
                )}

                {activeTab === 'pages' && (
                    <PagesManager
                        pages={pages}
                        onReload={loadContent}
                        showMessage={showMessage}
                    />
                )}

                {activeTab === 'content' && (
                    <PageContentManager
                        sections={sections}
                        onSave={(data, isUpdate) => {
                            if (data.section_id) {
                                // It's content
                                handleSave('content', data, isUpdate);
                            } else {
                                // It's a section
                                handleSave('sections', data, isUpdate);
                            }
                        }}
                        onDelete={(id, type) => {
                            if (type === 'content') {
                                handleDelete('content', id, 'content item');
                            } else {
                                handleDelete('sections', id, 'section');
                            }
                        }}
                        saving={saving}
                    />
                )}

                {activeTab === 'config' && (
                    <ConfigurationManager
                        config={config}
                        onSave={(item) => handleSave('config', item, true)}
                        showMessage={showMessage}
                        saving={saving}
                    />
                )}

                {activeTab === 'contact' && (
                    <ContactMessagesManager
                        messages={contactMessages}
                        onRefresh={loadContent}
                        showMessage={showMessage}
                    />
                )}
            </div>
        </div>
    );
};

// Carousel Manager Component
const CarouselManager = ({ items, onSave, onDelete, editingItem, setEditingItem, saving }) => {
    const [formData, setFormData] = useState({
        title: '', subtitle: '', image_path: '', link_url: '', link_text: '',
        display_order: items.length, is_active: 1
    });
    const [uploading, setUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState('');

    const handleEdit = (item) => {
        setFormData(item);
        setEditingItem(item.id);
        // Use the base URL from environment or default, and remove 'api' from the path
        if (item.image_path) {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';
            const imageUrl = `${baseUrl.replace('/api', '')}/${item.image_path}`;
            setImagePreview(imageUrl);
        } else {
            setImagePreview('');
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size should be less than 5MB');
            return;
        }

        try {
            setUploading(true);
            const formDataUpload = new FormData();
            formDataUpload.append('image', file);
            formDataUpload.append('folder', 'carousel');

            const token = localStorage.getItem('haazri_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api'}/landing_page_api.php/admin/upload-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataUpload
            });

            const result = await response.json();
            console.log('Upload result:', result);
            if (result.status === 'success') {
                const imagePath = result.image_path;
                console.log('Image path:', imagePath);
                setFormData(prev => ({ ...prev, image_path: imagePath }));
                // Use the base URL from environment or default, and remove 'api' from the path
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';
                const imageUrl = `${baseUrl.replace('/api', '')}/${imagePath}`;
                setImagePreview(imageUrl);
                console.log('Image preview set to:', imageUrl);
            } else {
                alert('Upload failed: ' + result.message);
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        console.log('Form submitted with data:', formData);

        // Validate that image_path is provided
        if (!formData.image_path) {
            alert('Please upload an image before saving');
            return;
        }

        console.log('Calling onSave with formData:', formData);
        onSave(formData);
    };

    const handleCancel = () => {
        setFormData({ title: '', subtitle: '', image_path: '', link_url: '', link_text: '', display_order: items.length, is_active: 1 });
        setEditingItem(null);
        setImagePreview('');
    };

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2>Carousel Items</h2>
                <button onClick={() => setEditingItem('new')} className="btn btn-success">
                    <i className="fas fa-plus"></i> Add Slide
                </button>
            </div>

            {(editingItem === 'new' || editingItem) && (
                <form onSubmit={handleSubmit} className="edit-form">
                    <h3>{editingItem === 'new' ? 'Add New Slide' : 'Edit Slide'}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Title *</label>
                            <input type="text" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                        </div>
                        <div className="form-group full-width">
                            <label>Carousel Image *</label>
                            <div className="image-upload-section">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    disabled={uploading}
                                    className="file-input"
                                    id="carousel-image-upload"
                                />
                                <label htmlFor="carousel-image-upload" className="file-input-label btn btn-secondary">
                                    <i className="fas fa-upload"></i> {uploading ? 'Uploading...' : 'Choose Image'}
                                </label>
                                {imagePreview && (
                                    <div className="image-preview">
                                        <img src={imagePreview} alt="Preview" />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form-group full-width">
                            <label>Subtitle</label>
                            <textarea value={formData.subtitle || ''} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} rows="2" />
                        </div>
                        <div className="form-group">
                            <label>Link URL</label>
                            <input type="text" value={formData.link_url || ''} onChange={(e) => setFormData({ ...formData, link_url: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>Link Text</label>
                            <input type="text" value={formData.link_text || ''} onChange={(e) => setFormData({ ...formData, link_text: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>Display Order</label>
                            <input type="number" value={formData.display_order || 0} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })} />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select value={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" disabled={saving} className="btn btn-primary">
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            <div className="items-list">
                {items.sort((a, b) => a.display_order - b.display_order).map((item) => (
                    <div key={item.id} className="item-card">
                        <div className="item-content">
                            <h4>{item.title}</h4>
                            <p>{item.subtitle}</p>
                            <small>Image: {item.image_path}</small>
                            <span className={`badge ${item.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                {item.is_active ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <div className="item-actions">
                            <button onClick={() => handleEdit(item)} className="btn-icon btn-edit">
                                <i className="fas fa-edit"></i>
                            </button>
                            <button onClick={() => onDelete(item.id)} className="btn-icon btn-delete">
                                <i className="fas fa-trash"></i>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Features, Testimonials, FAQ, and PageContent managers follow similar patterns
const FeaturesManager = ({ items, onSave, onDelete, editingItem, setEditingItem, saving }) => {
    const [formData, setFormData] = useState({
        icon_class: 'fas fa-star', title: '', description: '', display_order: items.length, is_active: 1
    });

    const handleEdit = (item) => { setFormData(item); setEditingItem(item.id); };
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    const handleCancel = () => { setFormData({ icon_class: 'fas fa-star', title: '', description: '', display_order: items.length, is_active: 1 }); setEditingItem(null); };

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2>Features</h2>
                <button onClick={() => setEditingItem('new')} className="btn btn-success">
                    <i className="fas fa-plus"></i> Add Feature
                </button>
            </div>

            {(editingItem === 'new' || editingItem) && (
                <form onSubmit={handleSubmit} className="edit-form">
                    <h3>{editingItem === 'new' ? 'Add New Feature' : 'Edit Feature'}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Icon Class *</label>
                            <input type="text" value={formData.icon_class || ''} onChange={(e) => setFormData({ ...formData, icon_class: e.target.value })} placeholder="fas fa-star" required />
                            <small>Font Awesome icon class (e.g., fas fa-check, fas fa-star)</small>
                        </div>
                        <div className="form-group">
                            <label>Title *</label>
                            <input type="text" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
                        </div>
                        <div className="form-group full-width">
                            <label>Description *</label>
                            <textarea value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" required />
                        </div>
                        <div className="form-group">
                            <label>Display Order</label>
                            <input type="number" value={formData.display_order || 0} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })} />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select value={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Saving...' : 'Save'}</button>
                        <button type="button" onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            <div className="items-list">
                {items.sort((a, b) => a.display_order - b.display_order).map((item) => (
                    <div key={item.id} className="item-card">
                        <div className="item-content">
                            <div className="feature-icon-preview">
                                <i className={item.icon_class}></i>
                            </div>
                            <h4>{item.title}</h4>
                            <p>{item.description}</p>
                            <span className={`badge ${item.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                {item.is_active ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <div className="item-actions">
                            <button onClick={() => handleEdit(item)} className="btn-icon btn-edit"><i className="fas fa-edit"></i></button>
                            <button onClick={() => onDelete(item.id)} className="btn-icon btn-delete"><i className="fas fa-trash"></i></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const TestimonialsManager = ({ items, onSave, onDelete, editingItem, setEditingItem, saving }) => {
    const [formData, setFormData] = useState({
        client_name: '', client_position: '', client_company: '', testimonial_text: '', rating: 5, display_order: items.length, is_active: 1
    });
    const [statusFilter, setStatusFilter] = useState('all');
    const [allTestimonials, setAllTestimonials] = useState([]);
    const [loadingTestimonials, setLoadingTestimonials] = useState(false);

    // Load testimonials with full status info
    useEffect(() => {
        loadTestimonialsWithStatus();
    }, []);

    const loadTestimonialsWithStatus = async () => {
        try {
            setLoadingTestimonials(true);
            const token = localStorage.getItem('haazri_token');
            const response = await axios.get(`${LANDING_API}/admin/testimonials/all-status`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.data.status === 'success') {
                setAllTestimonials(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load testimonials:', error);
        } finally {
            setLoadingTestimonials(false);
        }
    };

    const showNotification = (message, type = 'success') => {
        const notification = document.createElement('div');
        const bgColor = type === 'success' ? '#10b981' : '#ef4444';
        const icon = type === 'success' ? 'fa-check-circle' : 'fa-times-circle';

        notification.style.cssText = `position: fixed; top: 20px; right: 20px; background: ${bgColor}; color: white; padding: 15px 25px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 10000; font-weight: 600; animation: slideIn 0.3s ease;`;
        notification.innerHTML = `<i class="fas ${icon}" style="margin-right: 8px;"></i>${message}`;
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease';
            setTimeout(() => notification.remove(), 300);
        }, 3000);
    };

    const handleApprove = async (id) => {
        if (!window.confirm('Approve this testimonial?')) return;

        console.log('=== APPROVE TESTIMONIAL DEBUG ===');
        console.log('Testimonial ID:', id);
        console.log('API URL:', `${LANDING_API}/admin/testimonials/approve/${id}`);

        try {
            const token = localStorage.getItem('haazri_token');
            console.log('Token exists:', !!token);

            const response = await axios.post(
                `${LANDING_API}/admin/testimonials/approve/${id}`,
                {},
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            console.log('Approve Response:', response.data);

            if (response.data.status === 'success') {
                showNotification('Testimonial approved successfully!', 'success');
                loadTestimonialsWithStatus();
            } else {
                console.error('Approve failed:', response.data);
                showNotification('Failed: ' + response.data.message, 'error');
            }
        } catch (error) {
            console.error('=== APPROVE ERROR ===');
            console.error('Error:', error);
            console.error('Response:', error.response);
            console.error('Status:', error.response?.status);
            console.error('Data:', error.response?.data);

            showNotification('Failed to approve: ' + (error.response?.data?.message || error.message), 'error');
        }
    };

    const handleReject = async (id) => {
        const reason = prompt('Enter rejection reason:');
        if (!reason || reason.trim() === '') {
            showNotification('Rejection reason is required', 'error');
            return;
        }

        console.log('=== REJECT TESTIMONIAL DEBUG ===');
        console.log('Testimonial ID:', id);
        console.log('Rejection Reason:', reason);
        console.log('API URL:', `${LANDING_API}/admin/testimonials/reject/${id}`);

        try {
            const token = localStorage.getItem('haazri_token');
            console.log('Token exists:', !!token);

            const response = await axios.post(
                `${LANDING_API}/admin/testimonials/reject/${id}`,
                { rejection_reason: reason },
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );

            console.log('Reject Response:', response.data);

            if (response.data.status === 'success') {
                showNotification('Testimonial rejected', 'error');
                loadTestimonialsWithStatus();
            } else {
                console.error('Reject failed:', response.data);
                showNotification('Failed: ' + response.data.message, 'error');
            }
        } catch (error) {
            console.error('=== REJECT ERROR ===');
            console.error('Error:', error);
            console.error('Response:', error.response);
            console.error('Status:', error.response?.status);
            console.error('Data:', error.response?.data);

            showNotification('Failed to reject: ' + (error.response?.data?.message || error.message), 'error');
        }
    };

    const handleEdit = (item) => { setFormData(item); setEditingItem(item.id); };
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); loadTestimonialsWithStatus(); };
    const handleCancel = () => { setFormData({ client_name: '', client_position: '', client_company: '', testimonial_text: '', rating: 5, display_order: items.length, is_active: 1 }); setEditingItem(null); };

    // Filter testimonials based on selected status
    const filteredTestimonials = allTestimonials.filter(item => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'pending') return item.submission_status === 'pending';
        if (statusFilter === 'approved') return item.submission_status === 'approved';
        if (statusFilter === 'rejected') return item.submission_status === 'rejected';
        return true;
    });

    const pendingCount = allTestimonials.filter(t => t.submission_status === 'pending').length;

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2>Testimonials</h2>
                <button onClick={() => setEditingItem('new')} className="btn btn-success">
                    <i className="fas fa-plus"></i> Add Testimonial
                </button>
            </div>

            {/* Status Filter Tabs */}
            <div className="status-tabs" style={{ marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
                <button
                    onClick={() => setStatusFilter('all')}
                    className={statusFilter === 'all' ? 'tab-active' : 'tab-inactive'}
                    style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: statusFilter === 'all' ? '3px solid #14b8a6' : 'none', fontWeight: statusFilter === 'all' ? '600' : '400' }}
                >
                    All ({allTestimonials.length})
                </button>
                <button
                    onClick={() => setStatusFilter('pending')}
                    className={statusFilter === 'pending' ? 'tab-active' : 'tab-inactive'}
                    style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: statusFilter === 'pending' ? '3px solid #14b8a6' : 'none', fontWeight: statusFilter === 'pending' ? '600' : '400' }}
                >
                    Pending Review {pendingCount > 0 && <span style={{ background: '#fbbf24', color: '#92400e', padding: '2px 8px', borderRadius: '10px', fontSize: '0.85rem', marginLeft: '5px' }}>({pendingCount})</span>}
                </button>
                <button
                    onClick={() => setStatusFilter('approved')}
                    className={statusFilter === 'approved' ? 'tab-active' : 'tab-inactive'}
                    style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: statusFilter === 'approved' ? '3px solid #14b8a6' : 'none', fontWeight: statusFilter === 'approved' ? '600' : '400' }}
                >
                    Approved ({allTestimonials.filter(t => t.submission_status === 'approved').length})
                </button>
                <button
                    onClick={() => setStatusFilter('rejected')}
                    className={statusFilter === 'rejected' ? 'tab-active' : 'tab-inactive'}
                    style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', borderBottom: statusFilter === 'rejected' ? '3px solid #14b8a6' : 'none', fontWeight: statusFilter === 'rejected' ? '600' : '400' }}
                >
                    Rejected ({allTestimonials.filter(t => t.submission_status === 'rejected').length})
                </button>
            </div>

            {(editingItem === 'new' || editingItem) && (
                <form onSubmit={handleSubmit} className="edit-form">
                    <h3>{editingItem === 'new' ? 'Add New Testimonial' : 'Edit Testimonial'}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Client Name *</label>
                            <input type="text" value={formData.client_name || ''} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} required />
                        </div>
                        <div className="form-group">
                            <label>Position</label>
                            <input type="text" value={formData.client_position || ''} onChange={(e) => setFormData({ ...formData, client_position: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>Company</label>
                            <input type="text" value={formData.client_company || ''} onChange={(e) => setFormData({ ...formData, client_company: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <label>Rating (1-5)</label>
                            <select value={formData.rating || 5} onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}>
                                {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                            </select>
                        </div>
                        <div className="form-group full-width">
                            <label>Testimonial Text *</label>
                            <textarea value={formData.testimonial_text || ''} onChange={(e) => setFormData({ ...formData, testimonial_text: e.target.value })} rows="4" required />
                        </div>
                        <div className="form-group">
                            <label>Display Order</label>
                            <input type="number" value={formData.display_order || 0} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })} />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select value={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Saving...' : 'Save'}</button>
                        <button type="button" onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            <div className="items-list">
                {loadingTestimonials ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '10px' }}></i>
                        <p>Loading testimonials...</p>
                    </div>
                ) : filteredTestimonials.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                        <i className="fas fa-inbox" style={{ fontSize: '3rem', marginBottom: '15px' }}></i>
                        <p>No testimonials found for this status</p>
                    </div>
                ) : (
                    filteredTestimonials.sort((a, b) => a.display_order - b.display_order).map((item) => (
                        <div key={item.id} className="item-card" style={{ borderLeft: item.submission_status === 'pending' ? '4px solid #fbbf24' : item.submission_status === 'approved' ? '4px solid #10b981' : '4px solid #ef4444' }}>
                            <div className="item-content">
                                {/* Status Badge */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span className={`badge ${
                                        item.submission_status === 'pending' ? 'badge-warning' :
                                        item.submission_status === 'approved' ? 'badge-success' :
                                        'badge-danger'
                                    }`} style={{ fontSize: '0.85rem', padding: '5px 12px' }}>
                                        {item.submission_status === 'pending' ? 'Pending Review' :
                                         item.submission_status === 'approved' ? 'Approved' :
                                         'Rejected'}
                                    </span>
                                    <span className={`badge ${item.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                        {item.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div className="testimonial-rating" style={{ marginBottom: '10px' }}>
                                    {[...Array(item.rating)].map((_, i) => <i key={i} className="fas fa-star" style={{ color: '#fbbf24' }}></i>)}
                                </div>
                                <p className="testimonial-preview" style={{ fontStyle: 'italic', marginBottom: '15px' }}>"{item.testimonial_text}"</p>
                                <h4 style={{ marginBottom: '5px' }}>{item.client_name}</h4>
                                <small style={{ color: '#6b7280' }}>{item.client_position}{item.client_company ? `, ${item.client_company}` : ''}</small>

                                {/* Submission Info */}
                                {item.submitted_by_name && (
                                    <div style={{ marginTop: '15px', padding: '10px', background: '#f9fafb', borderRadius: '6px', fontSize: '0.85rem' }}>
                                        <div style={{ marginBottom: '5px' }}>
                                            <i className="fas fa-user" style={{ marginRight: '8px', color: '#6b7280' }}></i>
                                            <strong>Submitted by:</strong> {item.submitted_by_name}
                                        </div>
                                        {item.organization_name && (
                                            <div style={{ marginBottom: '5px' }}>
                                                <i className="fas fa-building" style={{ marginRight: '8px', color: '#6b7280' }}></i>
                                                <strong>Organization:</strong> {item.organization_name}
                                            </div>
                                        )}
                                        <div>
                                            <i className="fas fa-calendar" style={{ marginRight: '8px', color: '#6b7280' }}></i>
                                            <strong>Submitted:</strong> {new Date(item.submitted_at || item.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                )}

                                {/* Rejection Reason */}
                                {item.submission_status === 'rejected' && item.rejection_reason && (
                                    <div style={{ marginTop: '10px', padding: '10px', background: '#fef2f2', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem' }}>
                                        <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
                                        <strong>Rejection Reason:</strong> {item.rejection_reason}
                                    </div>
                                )}

                                {/* Review Info */}
                                {item.reviewed_by_name && (
                                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#9ca3af' }}>
                                        <i className="fas fa-check-circle" style={{ marginRight: '5px' }}></i>
                                        Reviewed by {item.reviewed_by_name} on {new Date(item.reviewed_at).toLocaleDateString()}
                                    </div>
                                )}
                            </div>

                            <div className="item-actions" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {/* Approve/Reject Buttons for Pending */}
                                {item.submission_status === 'pending' && (
                                    <>
                                        <button
                                            onClick={() => handleApprove(item.id)}
                                            className="btn btn-success btn-sm"
                                            style={{ padding: '6px 12px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                                            title="Approve testimonial"
                                        >
                                            <i className="fas fa-check"></i> Approve
                                        </button>
                                        <button
                                            onClick={() => handleReject(item.id)}
                                            className="btn btn-danger btn-sm"
                                            style={{ padding: '6px 12px', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                                            title="Reject testimonial"
                                        >
                                            <i className="fas fa-times"></i> Reject
                                        </button>
                                    </>
                                )}

                                {/* Edit/Delete Buttons */}
                                <button onClick={() => handleEdit(item)} className="btn-icon btn-edit" title="Edit testimonial">
                                    <i className="fas fa-edit"></i>
                                </button>
                                <button onClick={() => onDelete(item.id)} className="btn-icon btn-delete" title="Delete testimonial">
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const FAQManager = ({ items, onSave, onDelete, editingItem, setEditingItem, saving }) => {
    const [formData, setFormData] = useState({
        question: '', answer: '', display_order: items.length, is_active: 1
    });

    const handleEdit = (item) => { setFormData(item); setEditingItem(item.id); };
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    const handleCancel = () => { setFormData({ question: '', answer: '', display_order: items.length, is_active: 1 }); setEditingItem(null); };

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2>FAQ Items</h2>
                <button onClick={() => setEditingItem('new')} className="btn btn-success">
                    <i className="fas fa-plus"></i> Add FAQ
                </button>
            </div>

            {(editingItem === 'new' || editingItem) && (
                <form onSubmit={handleSubmit} className="edit-form">
                    <h3>{editingItem === 'new' ? 'Add New FAQ' : 'Edit FAQ'}</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Question *</label>
                            <input type="text" value={formData.question || ''} onChange={(e) => setFormData({ ...formData, question: e.target.value })} required />
                        </div>
                        <div className="form-group full-width">
                            <label>Answer *</label>
                            <textarea value={formData.answer || ''} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} rows="4" required />
                        </div>
                        <div className="form-group">
                            <label>Display Order</label>
                            <input type="number" value={formData.display_order || 0} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })} />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select value={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Saving...' : 'Save'}</button>
                        <button type="button" onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            <div className="items-list">
                {items.sort((a, b) => a.display_order - b.display_order).map((item) => (
                    <div key={item.id} className="item-card">
                        <div className="item-content">
                            <h4><i className="fas fa-question-circle"></i> {item.question}</h4>
                            <p>{item.answer}</p>
                            <span className={`badge ${item.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                {item.is_active ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <div className="item-actions">
                            <button onClick={() => handleEdit(item)} className="btn-icon btn-edit"><i className="fas fa-edit"></i></button>
                            <button onClick={() => onDelete(item.id)} className="btn-icon btn-delete"><i className="fas fa-trash"></i></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const BlogManager = ({ items, onSave, onDelete, editingItem, setEditingItem, saving }) => {
    const [formData, setFormData] = useState({
        title: '', slug: '', excerpt: '', content: '', author: '', category: '', tags: '',
        featured_image: '', video_url: '', media_type: 'image', publish_date: new Date().toISOString().split('T')[0],
        is_published: 1, display_order: items.length, is_active: 1
    });
    const [uploading, setUploading] = useState(false);
    const [imagePreview, setImagePreview] = useState('');

    const handleEdit = (item) => {
        setFormData(item);
        setEditingItem(item.id);
        if (item.featured_image) {
            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';
            const imageUrl = `${baseUrl.replace('/api', '')}/${item.featured_image}`;
            setImagePreview(imageUrl);
        } else {
            setImagePreview('');
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Image size should be less than 5MB');
            return;
        }

        try {
            setUploading(true);
            const formDataUpload = new FormData();
            formDataUpload.append('image', file);
            formDataUpload.append('folder', 'blog');

            const token = localStorage.getItem('haazri_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api'}/landing_page_api.php/admin/upload-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataUpload
            });

            const result = await response.json();
            if (result.status === 'success') {
                const imagePath = result.image_path;
                setFormData(prev => ({ ...prev, featured_image: imagePath }));
                const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api';
                const imageUrl = `${baseUrl.replace('/api', '')}/${imagePath}`;
                setImagePreview(imageUrl);
            } else {
                alert('Upload failed: ' + result.message);
            }
        } catch (error) {
            console.error('Upload error:', error);
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        setFormData({
            ...formData,
            title,
            slug: editingItem === 'new' ? generateSlug(title) : formData.slug
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleCancel = () => {
        setFormData({
            title: '', slug: '', excerpt: '', content: '', author: '', category: '', tags: '',
            featured_image: '', video_url: '', media_type: 'image', publish_date: new Date().toISOString().split('T')[0],
            is_published: 1, display_order: items.length, is_active: 1
        });
        setEditingItem(null);
        setImagePreview('');
    };

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2><i className="fas fa-newspaper"></i> Blog Posts</h2>
                <button onClick={() => setEditingItem('new')} className="btn btn-success">
                    <i className="fas fa-plus"></i> Add Blog Post
                </button>
            </div>

            {(editingItem === 'new' || editingItem) && (
                <form onSubmit={handleSubmit} className="edit-form">
                    <h3>{editingItem === 'new' ? 'Add New Blog Post' : 'Edit Blog Post'}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Title *</label>
                            <input
                                type="text"
                                value={formData.title || ''}
                                onChange={handleTitleChange}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Slug (URL-friendly) *</label>
                            <input
                                type="text"
                                value={formData.slug || ''}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                required
                            />
                            <small>Auto-generated from title, can be edited</small>
                        </div>
                        <div className="form-group">
                            <label>Media Type *</label>
                            <select
                                value={formData.media_type || 'image'}
                                onChange={(e) => setFormData({ ...formData, media_type: e.target.value })}
                            >
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                            </select>
                        </div>
                        {formData.media_type === 'image' ? (
                            <div className="form-group full-width">
                                <label>Featured Image</label>
                                <div className="image-upload-section">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        disabled={uploading}
                                        className="file-input"
                                        id="blog-image-upload"
                                    />
                                    <label htmlFor="blog-image-upload" className="file-input-label btn btn-secondary">
                                        <i className="fas fa-upload"></i> {uploading ? 'Uploading...' : 'Choose Image'}
                                    </label>
                                    {imagePreview && (
                                        <div className="image-preview">
                                            <img src={imagePreview} alt="Preview" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="form-group full-width">
                                <label>Video URL (YouTube or Vimeo)</label>
                                <input
                                    type="url"
                                    value={formData.video_url || ''}
                                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                                    placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
                                />
                                <small>Enter the full URL of the YouTube or Vimeo video</small>
                            </div>
                        )}
                        <div className="form-group full-width">
                            <label>Excerpt *</label>
                            <textarea
                                value={formData.excerpt || ''}
                                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                                rows="2"
                                required
                                placeholder="Brief summary for blog listing"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Content *</label>
                            <textarea
                                value={formData.content || ''}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                rows="10"
                                required
                                placeholder="Full blog post content (supports HTML)"
                            />
                        </div>
                        <div className="form-group">
                            <label>Author</label>
                            <input
                                type="text"
                                value={formData.author || ''}
                                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                                placeholder="Author name"
                            />
                        </div>
                        <div className="form-group">
                            <label>Category</label>
                            <input
                                type="text"
                                value={formData.category || ''}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                placeholder="e.g., Updates, Tips, News"
                            />
                        </div>
                        <div className="form-group">
                            <label>Tags</label>
                            <input
                                type="text"
                                value={formData.tags || ''}
                                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                                placeholder="Comma-separated tags"
                            />
                        </div>
                        <div className="form-group">
                            <label>Publish Date</label>
                            <input
                                type="date"
                                value={formData.publish_date || ''}
                                onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Display Order</label>
                            <input
                                type="number"
                                value={formData.display_order || 0}
                                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Published Status</label>
                            <select
                                value={formData.is_published}
                                onChange={(e) => setFormData({ ...formData, is_published: parseInt(e.target.value) })}
                            >
                                <option value="1">Published</option>
                                <option value="0">Draft</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Active Status</label>
                            <select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>
                    </div>
                    <div className="form-actions">
                        <button type="submit" disabled={saving} className="btn btn-primary">
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                    </div>
                </form>
            )}

            <div className="items-list">
                {items.sort((a, b) => b.id - a.id).map((item) => (
                    <div key={item.id} className="item-card blog-card">
                        <div className="item-content">
                            {item.featured_image && (
                                <div className="blog-thumbnail">
                                    <img
                                        src={`${(process.env.REACT_APP_API_URL || 'http://localhost/ehaazri/api').replace('/api', '')}/${item.featured_image}`}
                                        alt={item.title}
                                    />
                                </div>
                            )}
                            <div className="blog-info">
                                <h4>{item.title}</h4>
                                <p className="blog-excerpt">{item.excerpt}</p>
                                <div className="blog-meta">
                                    {item.author && <span><i className="fas fa-user"></i> {item.author}</span>}
                                    {item.category && <span><i className="fas fa-folder"></i> {item.category}</span>}
                                    {item.publish_date && <span><i className="fas fa-calendar"></i> {item.publish_date}</span>}
                                </div>
                                <div className="blog-badges">
                                    <span className={`badge ${item.is_published ? 'badge-success' : 'badge-warning'}`}>
                                        {item.is_published ? 'Published' : 'Draft'}
                                    </span>
                                    <span className={`badge ${item.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                        {item.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="item-actions">
                            <button onClick={() => handleEdit(item)} className="btn-icon btn-edit">
                                <i className="fas fa-edit"></i>
                            </button>
                            <button onClick={() => onDelete(item.id)} className="btn-icon btn-delete">
                                <i className="fas fa-trash"></i>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const PageContentManager = ({ sections, onSave, onDelete, saving }) => {
    const [editingSection, setEditingSection] = useState(null);
    const [editingContent, setEditingContent] = useState(null);
    const [sectionFormData, setSectionFormData] = useState({
        section_name: '', is_active: 1, display_order: 0
    });
    const [contentFormData, setContentFormData] = useState({
        content_key: '', content_type: 'text', content_value: '', display_order: 0, is_active: 1, metadata: ''
    });

    const handleEditSection = (section) => {
        setSectionFormData({
            id: section.id,
            section_name: section.section_name,
            is_active: section.is_active,
            display_order: section.display_order || 0
        });
        setEditingSection(section.id);
    };

    const handleEditContent = (sectionId, content) => {
        setContentFormData({
            id: content.id,
            section_id: sectionId,
            content_key: content.content_key,
            content_type: content.content_type,
            content_value: content.content_value,
            display_order: content.display_order,
            is_active: content.is_active,
            metadata: content.metadata || ''
        });
        setEditingContent(content.id);
    };

    const handleAddContent = (sectionId) => {
        setContentFormData({
            section_id: sectionId,
            content_key: '',
            content_type: 'text',
            content_value: '',
            display_order: 0,
            is_active: 1,
            metadata: ''
        });
        setEditingContent('new');
    };

    const handleSaveSection = () => {
        onSave(sectionFormData, editingSection);
        setEditingSection(null);
        setSectionFormData({ section_name: '', is_active: 1, display_order: 0 });
    };

    const handleSaveContent = () => {
        onSave(contentFormData, editingContent === 'new' ? null : editingContent);
        setEditingContent(null);
        setContentFormData({ content_key: '', content_type: 'text', content_value: '', display_order: 0, is_active: 1, metadata: '' });
    };

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2><i className="fas fa-file-alt"></i> Page Sections & Content</h2>
                <p>Manage homepage sections like Hero, How It Works, Why Replace, Industries, etc.</p>
            </div>

            {sections.length === 0 ? (
                <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <p>No sections found</p>
                </div>
            ) : (
                <div className="sections-list">
                    {sections.sort((a, b) => a.display_order - b.display_order).map((section) => (
                        <div key={section.id} className="section-card">
                            <div className="section-header-row">
                                <div>
                                    <h3>{section.section_name}</h3>
                                    <small style={{color: '#666'}}>Key: {section.section_key} | Order: {section.display_order}</small>
                                    <br/>
                                    <span className={`badge ${section.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                        {section.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleEditSection(section)}
                                    className="btn-icon btn-edit"
                                >
                                    <i className="fas fa-edit"></i>
                                </button>
                            </div>

                            {editingSection === section.id && (
                                <div className="edit-form">
                                    <h4>Edit Section</h4>
                                    <div className="form-group">
                                        <label>Section Name</label>
                                        <input
                                            type="text"
                                            value={sectionFormData.section_name}
                                            onChange={(e) => setSectionFormData({ ...sectionFormData, section_name: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Display Order</label>
                                        <input
                                            type="number"
                                            value={sectionFormData.display_order}
                                            onChange={(e) => setSectionFormData({ ...sectionFormData, display_order: parseInt(e.target.value) })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={sectionFormData.is_active == 1}
                                                onChange={(e) => setSectionFormData({ ...sectionFormData, is_active: e.target.checked ? 1 : 0 })}
                                            />
                                            Active
                                        </label>
                                    </div>
                                    <div className="form-actions">
                                        <button onClick={handleSaveSection} className="btn btn-primary" disabled={saving}>
                                            {saving ? 'Saving...' : 'Save Section'}
                                        </button>
                                        <button onClick={() => setEditingSection(null)} className="btn btn-secondary">
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="section-content">
                                <div className="content-header">
                                    <h4>Content Items</h4>
                                    <button
                                        onClick={() => handleAddContent(section.id)}
                                        className="btn btn-sm btn-primary"
                                    >
                                        <i className="fas fa-plus"></i> Add Content
                                    </button>
                                </div>

                                {section.content && section.content.length > 0 ? (
                                    <div className="content-items">
                                        {section.content.sort((a, b) => a.display_order - b.display_order).map((content) => (
                                            <div key={content.id} className="content-item">
                                                {editingContent === content.id ? (
                                                    <div className="edit-form">
                                                        <div className="form-row">
                                                            <div className="form-group">
                                                                <label>Content Key</label>
                                                                <input
                                                                    type="text"
                                                                    value={contentFormData.content_key}
                                                                    onChange={(e) => setContentFormData({ ...contentFormData, content_key: e.target.value })}
                                                                    placeholder="e.g., hero_title, stat_1_value"
                                                                />
                                                            </div>
                                                            <div className="form-group">
                                                                <label>Type</label>
                                                                <select
                                                                    value={contentFormData.content_type}
                                                                    onChange={(e) => setContentFormData({ ...contentFormData, content_type: e.target.value })}
                                                                >
                                                                    <option value="text">Text</option>
                                                                    <option value="html">HTML</option>
                                                                    <option value="image">Image</option>
                                                                    <option value="number">Number</option>
                                                                </select>
                                                            </div>
                                                            <div className="form-group">
                                                                <label>Order</label>
                                                                <input
                                                                    type="number"
                                                                    value={contentFormData.display_order}
                                                                    onChange={(e) => setContentFormData({ ...contentFormData, display_order: parseInt(e.target.value) })}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="form-group full-width">
                                                            <label>Content Value</label>
                                                            <textarea
                                                                value={contentFormData.content_value}
                                                                onChange={(e) => setContentFormData({ ...contentFormData, content_value: e.target.value })}
                                                                rows="3"
                                                            />
                                                        </div>
                                                        <div className="form-group full-width">
                                                            <label>Metadata (JSON format - optional)</label>
                                                            <textarea
                                                                value={contentFormData.metadata}
                                                                onChange={(e) => setContentFormData({ ...contentFormData, metadata: e.target.value })}
                                                                rows="2"
                                                                placeholder='{"icon":"💰","title":"Example Title"}'
                                                            />
                                                            <small style={{color: '#666', fontSize: '12px'}}>For Why Haazri problems, use: {`{"icon":"emoji","title":"Problem Title"}`}</small>
                                                        </div>
                                                        <div className="form-group">
                                                            <label>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={contentFormData.is_active == 1}
                                                                    onChange={(e) => setContentFormData({ ...contentFormData, is_active: e.target.checked ? 1 : 0 })}
                                                                />
                                                                Active
                                                            </label>
                                                        </div>
                                                        <div className="form-actions">
                                                            <button onClick={handleSaveContent} className="btn btn-primary btn-sm" disabled={saving}>
                                                                {saving ? 'Saving...' : 'Save'}
                                                            </button>
                                                            <button onClick={() => setEditingContent(null)} className="btn btn-secondary btn-sm">
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="content-info">
                                                            <strong>{content.content_key}</strong>
                                                            <span className="content-type-badge">{content.content_type}</span>
                                                            {!content.is_active && (
                                                                <span className="badge badge-secondary">Inactive</span>
                                                            )}
                                                            <div className="content-value">{content.content_value}</div>
                                                        </div>
                                                        <div className="content-actions">
                                                            <button
                                                                onClick={() => handleEditContent(section.id, content)}
                                                                className="btn-icon btn-edit"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            <button
                                                                onClick={() => onDelete(content.id, 'content')}
                                                                className="btn-icon btn-delete"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="no-content">No content items yet</p>
                                )}

                                {editingContent === 'new' && contentFormData.section_id === section.id && (
                                    <div className="edit-form new-content-form">
                                        <h5>Add New Content</h5>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label>Content Key</label>
                                                <input
                                                    type="text"
                                                    value={contentFormData.content_key}
                                                    onChange={(e) => setContentFormData({ ...contentFormData, content_key: e.target.value })}
                                                    placeholder="e.g., hero_title, stat_1_value"
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Type</label>
                                                <select
                                                    value={contentFormData.content_type}
                                                    onChange={(e) => setContentFormData({ ...contentFormData, content_type: e.target.value })}
                                                >
                                                    <option value="text">Text</option>
                                                    <option value="html">HTML</option>
                                                    <option value="image">Image</option>
                                                    <option value="number">Number</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Order</label>
                                                <input
                                                    type="number"
                                                    value={contentFormData.display_order}
                                                    onChange={(e) => setContentFormData({ ...contentFormData, display_order: parseInt(e.target.value) })}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Content Value</label>
                                            <textarea
                                                value={contentFormData.content_value}
                                                onChange={(e) => setContentFormData({ ...contentFormData, content_value: e.target.value })}
                                                rows="3"
                                            />
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Metadata (JSON format - optional)</label>
                                            <textarea
                                                value={contentFormData.metadata}
                                                onChange={(e) => setContentFormData({ ...contentFormData, metadata: e.target.value })}
                                                rows="2"
                                                placeholder='{"icon":"💰","title":"Example Title"}'
                                            />
                                            <small style={{color: '#666', fontSize: '12px'}}>For Why Haazri problems, use: {`{"icon":"emoji","title":"Problem Title"}`}</small>
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={contentFormData.is_active == 1}
                                                    onChange={(e) => setContentFormData({ ...contentFormData, is_active: e.target.checked ? 1 : 0 })}
                                                />
                                                Active
                                            </label>
                                        </div>
                                        <div className="form-actions">
                                            <button onClick={handleSaveContent} className="btn btn-primary btn-sm" disabled={saving}>
                                                {saving ? 'Saving...' : 'Add Content'}
                                            </button>
                                            <button onClick={() => setEditingContent(null)} className="btn btn-secondary btn-sm">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// Pages Manager Component (About, Privacy, Terms)
const PagesManager = ({ pages, onReload, showMessage }) => {
    const [selectedPage, setSelectedPage] = useState(null);
    const [pageData, setPageData] = useState(null);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingSection, setEditingSection] = useState(null);

    const loadPageData = async (pageId) => {
        try {
            setLoading(true);
            const response = await landingPageService.getPageSections(pageId);
            if (response.status === 'success') {
                setSections(response.data || []);
                const page = pages.find(p => p.id === pageId);
                setPageData(page);
            }
        } catch (error) {
            showMessage('error', 'Failed to load page: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePageSelect = (page) => {
        setSelectedPage(page.id);
        loadPageData(page.id);
    };

    const handleSaveSection = async (section) => {
        try {
            setSaving(true);
            let response;
            if (section.id) {
                response = await landingPageService.updatePageSection(section.id, section);
            } else {
                response = await landingPageService.createPageSection({
                    ...section,
                    page_id: selectedPage,
                    display_order: sections.length,
                    is_active: 1
                });
            }

            if (response.status === 'success') {
                showMessage('success', 'Section saved successfully');
                loadPageData(selectedPage);
                setEditingSection(null);
            }
        } catch (error) {
            showMessage('error', 'Failed to save section: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSection = async (sectionId) => {
        if (!window.confirm('Are you sure you want to delete this section?')) return;

        try {
            setSaving(true);
            const response = await landingPageService.deletePageSection(sectionId);
            if (response.status === 'success') {
                showMessage('success', 'Section deleted successfully');
                loadPageData(selectedPage);
            }
        } catch (error) {
            showMessage('error', 'Failed to delete section: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="manager-section">
            <div className="section-header">
                <h2>Manage Pages (About / Privacy / Terms)</h2>
                <p>Select a page to edit its sections</p>
            </div>

            <div className="pages-grid">
                {pages.map(page => (
                    <button
                        key={page.id}
                        onClick={() => handlePageSelect(page)}
                        className={`page-card ${selectedPage === page.id ? 'active' : ''}`}
                    >
                        <i className="fas fa-file-alt"></i>
                        <h3>{page.page_title}</h3>
                        <p>{page.page_subtitle}</p>
                        <span className={`badge ${page.is_active ? 'badge-success' : 'badge-secondary'}`}>
                            {page.is_active ? 'Active' : 'Inactive'}
                        </span>
                    </button>
                ))}
            </div>

            {selectedPage && pageData && (
                <div className="page-editor">
                    <div className="page-editor-header">
                        <h3>Editing: {pageData.page_title}</h3>
                        <button
                            onClick={() => setEditingSection('new')}
                            className="btn btn-success"
                            disabled={saving}
                        >
                            <i className="fas fa-plus"></i> Add Section
                        </button>
                    </div>

                    {editingSection && (
                        <SectionEditor
                            section={editingSection === 'new' ? {} : sections.find(s => s.id === editingSection)}
                            onSave={handleSaveSection}
                            onCancel={() => setEditingSection(null)}
                            saving={saving}
                        />
                    )}

                    {loading ? (
                        <div className="loading-container">
                            <div className="spinner"></div>
                            <p>Loading sections...</p>
                        </div>
                    ) : (
                        <div className="sections-list">
                            {sections.length === 0 && !editingSection && (
                                <div className="empty-state">
                                    <i className="fas fa-inbox"></i>
                                    <p>No sections yet. Click "Add Section" to create one.</p>
                                </div>
                            )}
                            {sections.sort((a, b) => a.display_order - b.display_order).map(section => (
                                <div key={section.id} className="section-card">
                                    <div className="section-header-row">
                                        <h4>{section.section_title}</h4>
                                        <div className="section-actions">
                                            <button
                                                onClick={() => setEditingSection(section.id)}
                                                className="btn-icon btn-edit"
                                                disabled={saving}
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteSection(section.id)}
                                                className="btn-icon btn-delete"
                                                disabled={saving}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="section-preview">
                                        <span className="section-type-badge">{section.section_type}</span>
                                        <p>{stripHtml(section.section_content).substring(0, 200)}...</p>
                                    </div>
                                    <span className={`badge ${section.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                        {section.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Section Editor Component
const SectionEditor = ({ section, onSave, onCancel, saving }) => {
    const [formData, setFormData] = useState({
        section_title: section.section_title || '',
        section_content: section.section_content || '',
        section_type: section.section_type || 'text',
        display_order: section.display_order || 0,
        is_active: section.is_active !== undefined ? section.is_active : 1,
        metadata: section.metadata || {}
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ ...section, ...formData });
    };

    return (
        <form onSubmit={handleSubmit} className="edit-form section-edit-form">
            <h3>{section.id ? 'Edit Section' : 'Add New Section'}</h3>
            <div className="form-grid">
                <div className="form-group">
                    <label>Section Title *</label>
                    <input
                        type="text"
                        value={formData.section_title}
                        onChange={(e) => setFormData({ ...formData, section_title: e.target.value })}
                        required
                    />
                </div>
                <div className="form-group">
                    <label>Section Type</label>
                    <select
                        value={formData.section_type}
                        onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
                    >
                        <option value="text">Text</option>
                        <option value="list">List</option>
                        <option value="grid">Grid</option>
                        <option value="hero">Hero</option>
                    </select>
                </div>
                <div className="form-group full-width">
                    <label>Section Content *</label>
                    <textarea
                        value={formData.section_content}
                        onChange={(e) => setFormData({ ...formData, section_content: e.target.value })}
                        rows="8"
                        required
                        placeholder="Use \\n for new lines. For lists, separate items with \\n- "
                    />
                    <small>Tip: Use \n for line breaks. For lists, start each item with a dash (-).</small>
                </div>
                <div className="form-group">
                    <label>Display Order</label>
                    <input
                        type="number"
                        value={formData.display_order}
                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                    />
                </div>
                <div className="form-group">
                    <label>Status</label>
                    <select
                        value={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value) })}
                    >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                    </select>
                </div>
            </div>
            <div className="form-actions">
                <button type="submit" disabled={saving} className="btn btn-primary">
                    {saving ? 'Saving...' : 'Save Section'}
                </button>
                <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={saving}>
                    Cancel
                </button>
            </div>
        </form>
    );
};

// Configuration Manager Component
const ConfigurationManager = ({ config, onSave, showMessage, saving }) => {
    const [configValues, setConfigValues] = useState({});

    useEffect(() => {
        // Initialize config values from props
        const values = {};
        config.forEach(item => {
            values[item.config_key] = item.config_value;
        });
        setConfigValues(values);
    }, [config]);

    const handleChange = (key, value) => {
        setConfigValues(prev => ({ ...prev, [key]: value }));
    };

    const handleSaveConfig = async (key, value) => {
        const configItem = config.find(item => item.config_key === key);
        if (!configItem) return;

        await onSave({
            id: configItem.id,
            config_value: value
        });
    };

    const handleFileUpload = async (configKey, file) => {
        const token = localStorage.getItem('haazri_token');
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(
                `${API_URL}/landing_page_api.php/admin/config/upload/${configKey}`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.status === 'success') {
                showMessage('success', response.data.message);
                // Reload config to get new file path
                const configResponse = await axios.get(`${API_URL}/landing_page_api.php/admin/config`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (configResponse.data.status === 'success') {
                    // Update only the config state
                    const newConfig = configResponse.data.data || [];
                    const values = {};
                    newConfig.forEach(item => {
                        values[item.config_key] = item.config_value;
                    });
                    setConfigValues(values);
                }
            } else {
                showMessage('error', response.data.message);
            }
        } catch (error) {
            showMessage('error', 'Upload failed: ' + (error.response?.data?.message || error.message));
        }
    };

    const groupedConfig = {
        branding: config.filter(c => c.config_key.startsWith('site_logo') || c.config_key.startsWith('site_favicon')),
        stats: config.filter(c => c.config_key.startsWith('stats_')),
        links: config.filter(c => c.config_key.startsWith('link_')),
        cta: config.filter(c => c.config_key.startsWith('cta_')),
        recaptcha: config.filter(c => c.config_key.startsWith('recaptcha_')),
        contact: config.filter(c => c.config_key.startsWith('contact_form_')),
        social: config.filter(c => c.config_key.startsWith('social_')),
        seo: config.filter(c => c.config_key.startsWith('seo_')),
        analytics: config.filter(c => c.config_key.startsWith('analytics_')),
        contactInfo: config.filter(c => c.config_key.startsWith('contact_email') || c.config_key.startsWith('contact_phone') || c.config_key.startsWith('contact_website') || c.config_key.startsWith('contact_address')),
        footer: config.filter(c => c.config_key.startsWith('copyright_') || c.config_key.startsWith('footer_'))
    };

    const getLabel = (key) => {
        return key
            .replace(/^(stats_|link_|cta_|recaptcha_|contact_form_|social_|seo_|analytics_|contact_|copyright_|footer_)/, '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <div className="config-manager">
            <div className="section-header">
                <h2>Configuration Settings</h2>
                <p>Manage statistics, links, and CTA text for the landing page</p>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-palette"></i> Branding (Logo & Favicon)</h3>
                <p className="section-description">Upload and manage logo and favicon for the landing page</p>
                <div className="config-grid">
                    {groupedConfig.branding.map(item => (
                        <div key={item.id} className="config-item branding-item">
                            <label>{getLabel(item.config_key)}</label>
                            {item.config_value && (
                                <div className="branding-preview">
                                    <img
                                        src={`${API_URL.replace('/api', '')}/${item.config_value}`}
                                        alt={item.config_key}
                                        style={{
                                            maxWidth: item.config_key === 'site_logo' ? '200px' : '32px',
                                            maxHeight: item.config_key === 'site_logo' ? '80px' : '32px',
                                            objectFit: 'contain',
                                            marginBottom: '10px'
                                        }}
                                    />
                                </div>
                            )}
                            <div className="file-upload-wrapper">
                                <input
                                    type="file"
                                    accept={item.config_key === 'site_logo' ? '.png,.jpg,.jpeg,.svg,.webp' : '.ico,.png'}
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) handleFileUpload(item.config_key, file);
                                    }}
                                    id={`file-${item.config_key}`}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor={`file-${item.config_key}`} className="btn btn-sm btn-secondary file-upload-btn">
                                    <i className="fas fa-upload"></i> Upload {item.config_key === 'site_logo' ? 'Logo' : 'Favicon'}
                                </label>
                                <small className="config-description">
                                    {item.config_key === 'site_logo'
                                        ? 'Accepted: PNG, JPG, SVG, WebP (Max 2MB)'
                                        : 'Accepted: ICO, PNG (Max 2MB)'}
                                </small>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-chart-bar"></i> Statistics</h3>
                <p className="section-description">These statistics are displayed on the pricing section of the landing page</p>
                <div className="config-grid">
                    {groupedConfig.stats.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type="number"
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-link"></i> Links</h3>
                <p className="section-description">Configure navigation links for the landing page</p>
                <div className="config-grid">
                    {groupedConfig.links.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type="text"
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                    placeholder="/haazri-frontend/#/..."
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-mouse-pointer"></i> Call-to-Action Text</h3>
                <p className="section-description">Customize button text for CTAs on the landing page</p>
                <div className="config-grid">
                    {groupedConfig.cta.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type="text"
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                    placeholder="Button text..."
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-shield-alt"></i> reCAPTCHA Settings</h3>
                <p className="section-description">Google reCAPTCHA v3 configuration for contact form protection</p>
                <div className="config-grid">
                    {groupedConfig.recaptcha.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type={item.config_key.includes('secret') ? 'password' : 'text'}
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                    placeholder={item.description}
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-envelope"></i> Contact Form Settings</h3>
                <p className="section-description">Email notifications and reply settings for contact form submissions</p>
                <div className="config-grid">
                    {groupedConfig.contact.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type={item.config_key.includes('email') ? 'email' : 'text'}
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                    placeholder={item.description}
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-share-alt"></i> Social Media Links</h3>
                <p className="section-description">Configure social media profile links displayed on the landing page</p>
                <div className="config-grid">
                    {groupedConfig.social.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type="url"
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                    placeholder={item.description || 'https://...'}
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-search"></i> SEO Settings</h3>
                <p className="section-description">Search engine optimization meta tags and site information</p>
                <div className="config-grid">
                    {groupedConfig.seo.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                {item.config_key.includes('description') ? (
                                    <textarea
                                        value={configValues[item.config_key] || ''}
                                        onChange={(e) => handleChange(item.config_key, e.target.value)}
                                        placeholder={item.description}
                                        rows="3"
                                        style={{ resize: 'vertical' }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={configValues[item.config_key] || ''}
                                        onChange={(e) => handleChange(item.config_key, e.target.value)}
                                        placeholder={item.description}
                                    />
                                )}
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-chart-line"></i> Analytics & Tracking</h3>
                <p className="section-description">Google Analytics, Meta Pixel, and other tracking codes</p>
                <div className="config-grid">
                    {groupedConfig.analytics.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                <input
                                    type="text"
                                    value={configValues[item.config_key] || ''}
                                    onChange={(e) => handleChange(item.config_key, e.target.value)}
                                    placeholder={item.description}
                                />
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-address-card"></i> Contact Information</h3>
                <p className="section-description">Configure contact details displayed on the landing page</p>
                <div className="config-grid">
                    {groupedConfig.contactInfo.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                {item.config_key.includes('address') ? (
                                    <textarea
                                        value={configValues[item.config_key] || ''}
                                        onChange={(e) => handleChange(item.config_key, e.target.value)}
                                        placeholder={item.description}
                                        rows="3"
                                        style={{ resize: 'vertical' }}
                                    />
                                ) : (
                                    <input
                                        type={item.config_key.includes('email') ? 'email' : 'text'}
                                        value={configValues[item.config_key] || ''}
                                        onChange={(e) => handleChange(item.config_key, e.target.value)}
                                        placeholder={item.description}
                                    />
                                )}
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="config-section">
                <h3><i className="fas fa-copyright"></i> Copyright & Footer</h3>
                <p className="section-description">Configure copyright text and footer information</p>
                <div className="config-grid">
                    {groupedConfig.footer.map(item => (
                        <div key={item.id} className="config-item">
                            <label>{getLabel(item.config_key)}</label>
                            <div className="input-with-save">
                                {item.config_key.includes('description') ? (
                                    <textarea
                                        value={configValues[item.config_key] || ''}
                                        onChange={(e) => handleChange(item.config_key, e.target.value)}
                                        placeholder={item.description}
                                        rows="3"
                                        style={{ resize: 'vertical' }}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={configValues[item.config_key] || ''}
                                        onChange={(e) => handleChange(item.config_key, e.target.value)}
                                        placeholder={item.description}
                                    />
                                )}
                                <button
                                    onClick={() => handleSaveConfig(item.config_key, configValues[item.config_key])}
                                    disabled={saving || configValues[item.config_key] === item.config_value}
                                    className="btn btn-sm btn-primary"
                                >
                                    <i className="fas fa-save"></i> Save
                                </button>
                            </div>
                            {item.description && (
                                <small className="config-description">{item.description}</small>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Contact Messages Manager Component
const ContactMessagesManager = ({ messages, onRefresh, showMessage }) => {
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sending, setSending] = useState(false);

    const LANDING_API = process.env.REACT_APP_API_URL ?
        `${process.env.REACT_APP_API_URL}/landing_page_api.php` :
        'http://localhost/ehaazri/api/landing_page_api.php';

    const getAuthHeaders = () => {
        const token = localStorage.getItem('haazri_token') || localStorage.getItem('admin_token');
        return {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getAttachmentUrl = (filePath) => {
        if (!filePath || typeof filePath !== 'string') return '#';
        if (/^https?:\/\//i.test(filePath)) return filePath;

        const apiBase = LANDING_API.replace(/\/landing_page_api\.php$/, '');
        const normalizedPath = filePath.replace(/^\/+/, '');
        return `${apiBase}/${normalizedPath}`;
    };

    const getAttachments = (message) => {
        const raw = message?.attachments;
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string' && raw.trim() !== '') {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                return [];
            }
        }
        return [];
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'new': { color: 'primary', icon: 'envelope' },
            'read': { color: 'info', icon: 'envelope-open' },
            'replied': { color: 'success', icon: 'reply' },
            'archived': { color: 'secondary', icon: 'archive' }
        };
        const s = statusMap[status] || statusMap['new'];
        return (
            <span className={`badge badge-${s.color}`}>
                <i className={`fas fa-${s.icon}`}></i> {status}
            </span>
        );
    };

    const handleViewMessage = async (message) => {
        setSelectedMessage(message);
        setReplyText('');

        // Mark as read if new
        if (message.status === 'new') {
            try {
                await axios.put(
                    `${LANDING_API}/admin/contact/messages/${message.id}/status`,
                    { status: 'read' },
                    getAuthHeaders()
                );
                setSelectedMessage({ ...message, status: 'read' });
            } catch (error) {
                console.error('Error marking message as read:', error);
            }
        }
    };

    const handleSendReply = async () => {
        if (!replyText.trim() || !selectedMessage) return;

        setSending(true);
        try {
            const response = await axios.post(
                `${LANDING_API}/admin/contact/messages/${selectedMessage.id}/reply`,
                { reply_message: replyText },
                getAuthHeaders()
            );

            if (response.data.status === 'success') {
                showMessage('success', 'Reply sent successfully');
                setReplyText('');
                setSelectedMessage(null);
                onRefresh();
            } else {
                showMessage('error', response.data.message || 'Failed to send reply');
            }
        } catch (error) {
            showMessage('error', 'Error sending reply: ' + error.message);
        } finally {
            setSending(false);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const response = await axios.put(
                `${LANDING_API}/admin/contact/messages/${id}/status`,
                { status },
                getAuthHeaders()
            );

            if (response.data.status === 'success') {
                showMessage('success', 'Status updated');
                onRefresh();
            }
        } catch (error) {
            showMessage('error', 'Error updating status: ' + error.message);
        }
    };

    const filteredMessages = filterStatus === 'all'
        ? messages
        : messages.filter(m => m.status === filterStatus);

    return (
        <div className="contact-messages-manager">
            <div className="section-header">
                <h2><i className="fas fa-envelope"></i> Contact Messages</h2>
                <p>View and reply to contact form submissions</p>
            </div>

            <div className="messages-filter">
                <button
                    type="button"
                    onClick={() => setFilterStatus('all')}
                    className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline'}`}
                >
                    All ({messages.length})
                </button>
                <button
                    type="button"
                    onClick={() => setFilterStatus('new')}
                    className={`btn btn-sm ${filterStatus === 'new' ? 'btn-primary' : 'btn-outline'}`}
                >
                    New ({messages.filter(m => m.status === 'new').length})
                </button>
                <button
                    type="button"
                    onClick={() => setFilterStatus('read')}
                    className={`btn btn-sm ${filterStatus === 'read' ? 'btn-primary' : 'btn-outline'}`}
                >
                    Read ({messages.filter(m => m.status === 'read').length})
                </button>
                <button
                    type="button"
                    onClick={() => setFilterStatus('replied')}
                    className={`btn btn-sm ${filterStatus === 'replied' ? 'btn-primary' : 'btn-outline'}`}
                >
                    Replied ({messages.filter(m => m.status === 'replied').length})
                </button>
            </div>

            <div className="messages-grid">
                {/* Messages List */}
                <div className="messages-list">
                    {filteredMessages.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-inbox"></i>
                            <p>No messages found</p>
                        </div>
                    ) : (
                        filteredMessages.map(message => {
                            const currentStatus = selectedMessage?.id === message.id ? selectedMessage.status : message.status;
                            return (
                            <div
                                key={message.id}
                                className={`message-item ${selectedMessage?.id === message.id ? 'active' : ''} ${currentStatus === 'new' ? 'unread' : ''}`}
                                onClick={() => handleViewMessage(message)}
                            >
                                <div className="message-header">
                                    <strong>{message.name}</strong>
                                    {getStatusBadge(currentStatus)}
                                </div>
                                <div className="message-subject">{message.subject}</div>
                                <div className="message-preview">
                                    {message.message.substring(0, 100)}...
                                </div>
                                <div className="message-meta">
                                    <small>
                                        <i className="fas fa-clock"></i> {formatDate(message.created_at)}
                                    </small>
                                </div>
                            </div>
                        )})
                    )}
                </div>

                {/* Message Details */}
                <div className="message-details">
                    {selectedMessage ? (
                        <>
                            <div className="message-details-header">
                                <h3>{selectedMessage.subject}</h3>
                                <div className="message-actions">
                                    {getStatusBadge(selectedMessage.status)}
                                    {selectedMessage.status !== 'archived' && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => handleUpdateStatus(selectedMessage.id, 'archived')}
                                        >
                                            <i className="fas fa-archive"></i> Archive
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline"
                                        onClick={() => setSelectedMessage(null)}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="message-info">
                                <div className="info-row">
                                    <strong>From:</strong> {selectedMessage.name} ({selectedMessage.email})
                                </div>
                                {selectedMessage.phone && (
                                    <div className="info-row">
                                        <strong>Phone:</strong> {selectedMessage.phone}
                                    </div>
                                )}
                                <div className="info-row">
                                    <strong>Received:</strong> {formatDate(selectedMessage.created_at)}
                                </div>
                                <div className="info-row">
                                    <strong>IP Address:</strong> {selectedMessage.ip_address}
                                </div>
                            </div>

                            <div className="message-content">
                                <h4>Message:</h4>
                                <div className="message-text">
                                    {selectedMessage.message}
                                </div>
                            </div>

                            {getAttachments(selectedMessage).length > 0 && (
                                <div className="message-attachments">
                                    <h4>Attachments:</h4>
                                    {getAttachments(selectedMessage).map((file, idx) => (
                                        <div key={idx} className="attachment-item">
                                            <i className="fas fa-file"></i>
                                            <a
                                                href={getAttachmentUrl(file.path)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="View attachment"
                                            >
                                                {file.original_name}
                                            </a>
                                            <small>({(file.size / 1024).toFixed(2)} KB)</small>
                                            <a
                                                href={getAttachmentUrl(file.path)}
                                                download={file.original_name}
                                                className="btn btn-sm btn-outline"
                                                style={{ marginLeft: '8px' }}
                                                title="Download attachment"
                                            >
                                                <i className="fas fa-download"></i>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {selectedMessage.reply_message && (
                                <div className="previous-reply">
                                    <h4><i className="fas fa-reply"></i> Previous Reply:</h4>
                                    <div className="reply-text">
                                        {selectedMessage.reply_message}
                                    </div>
                                    <small>Sent on {formatDate(selectedMessage.replied_at)}</small>
                                </div>
                            )}

                            <div className="reply-section">
                                <h4>Send Reply:</h4>
                                <textarea
                                    className="reply-textarea"
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type your reply here..."
                                    rows="6"
                                />
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSendReply}
                                    disabled={!replyText.trim() || sending}
                                >
                                    <i className="fas fa-paper-plane"></i>
                                    {sending ? 'Sending...' : 'Send Reply'}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="empty-state">
                            <i className="fas fa-envelope-open"></i>
                            <p>Select a message to view details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LandingPageManager;
